import * as Sentry from '@sentry/react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/tokenManager';
import { getApiBaseUrl } from '../utils/apiConfig';

// API configurat cu adresa backendului din .env
const _apiBase = getApiBaseUrl();
const API = axios.create({
  baseURL: _apiBase,
});

// Token din sesiunea Supabase (inclusiv native): mai sigur decât doar parsarea manuală a localStorage.
//
// Coalescing: mai multe request-uri pornite simultan (ex. home declanșează ~6-8
// fetch-uri) ar apela fiecare `getSession()` separat, fiecare luând lock-ul intern
// Supabase. Refolosim UN SINGUR promise cât timp e in-flight, apoi îl resetăm prin
// `.finally` → tokenul nu e niciodată mai vechi decât burst-ul curent, iar refresh-ul
// de token și fallback-ul rămân neschimbate.
let _sessionPromise = null;
const getSessionCoalesced = () => {
  if (_sessionPromise) return _sessionPromise;
  _sessionPromise = (async () => {
    const { supabase } = await import('../lib/supabase');
    return supabase.auth.getSession();
  })().finally(() => { _sessionPromise = null; });
  return _sessionPromise;
};

API.interceptors.request.use(async (config) => {
  try {
    const { data: { session } } = await getSessionCoalesced();
    if (session?.access_token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${session.access_token}`;
      config.headers['X-Auth-Token'] = session.access_token;
    }
  } catch {
    const headers = getAuthHeaders();
    Object.assign(config.headers || {}, headers);
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Retry automat pentru request-uri care esueaza din motive tranziente:
// - network error (fara response; timeouts, DNS, conexiune mobila instabila)
// - status 5xx
// NU facem retry pentru 4xx (erori de auth/permisiuni — nu se rezolva prin retry).
const RETRYABLE_METHODS = new Set(['get', 'head', 'options']);
const MAX_RETRIES = 2;
const RETRY_BACKOFF_MS = [500, 1500];

const shouldRetry = (error) => {
  const cfg = error?.config || {};
  const method = String(cfg.method || 'get').toLowerCase();
  if (!RETRYABLE_METHODS.has(method)) return false;
  const retried = cfg.__retryCount || 0;
  if (retried >= MAX_RETRIES) return false;
  // Network error: no response
  if (!error.response) return true;
  // Server error
  if (error.response.status >= 500 && error.response.status < 600) return true;
  return false;
};

// Interceptor pentru API errors → retry + Sentry
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;
    if (cfg && shouldRetry(error)) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      const delay = RETRY_BACKOFF_MS[cfg.__retryCount - 1] || 2000;
      await new Promise((r) => setTimeout(r, delay));
      return API(cfg);
    }

    Sentry.captureException(error, {
      tags: {
        api_url: error.config?.url,
        api_method: error.config?.method,
        status_code: error.response?.status,
      },
      extra: {
        response_data: error.response?.data,
      },
    });
    return Promise.reject(error);
  }
);

// Meditații, tehnici, beneficii

// funcții shuffle determinist pentru randomizare continut per user/per zi
function seedRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function shuffleWithSeed(array, seed) {
  let arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const rand = seedRandom(seed + i);
    const j = Math.floor(rand * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ─── In-memory list cache ────────────────────────────────────────────────────
// Evită refetch-uri inutile când utilizatorul navighează între pagini de listă
// și meditație și înapoi. Doar liste „relativ statice” pe parcursul sesiunii.
// TTL conservator (5 min) — suficient cât userul navighează, scurt cât să nu
// apară conținut învechit la sesiuni lungi.
const LIST_CACHE_TTL_MS = 5 * 60 * 1000;
const _listCache = new Map(); // key → { value, expiresAt, promise }

const _withListCache = (key, ttl, loader) => {
  const now = Date.now();
  const hit = _listCache.get(key);
  if (hit && hit.expiresAt > now && hit.value !== undefined) {
    return Promise.resolve(hit.value);
  }
  // request-coalescing: dacă există un fetch în curs pentru aceeași cheie,
  // returnăm aceeași promisiune ca să nu pornim N requesturi paralele.
  if (hit && hit.promise) return hit.promise;

  const promise = loader()
    .then((value) => {
      _listCache.set(key, { value, expiresAt: Date.now() + ttl });
      return value;
    })
    .catch((err) => {
      _listCache.delete(key);
      throw err;
    });

  _listCache.set(key, { ...(hit || {}), promise });
  return promise;
};

export const clearListCaches = (keyPrefix) => {
  if (!keyPrefix) {
    _listCache.clear();
    return;
  }
  for (const k of _listCache.keys()) {
    if (k.startsWith(keyPrefix)) _listCache.delete(k);
  }
};

export const fetchMeditatii = async () => {
  return _withListCache('meditatii', LIST_CACHE_TTL_MS, async () => {
    // Backend-ul returnează toate meditațiile complet
    // Frontend-ul decide ce să afișeze în funcție de statusul premium al user-ului
    const response = await API.get('/meditatii');
    const data = response.data;

    // seed = data curentă (ex: 20251030)
    const today = new Date().toISOString().slice(0, 10);
    const seed = parseInt(today.replace(/-/g, ""));

    // returnăm direct randomizat
    return shuffleWithSeed(data, seed);
  });
};

export const fetchTehnici = async () => {
  return _withListCache('tehnici', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/tehnici');
    const data = response.data || [];
    return [...data].sort((a, b) => Number(a.id) - Number(b.id));
  });
};

export const fetchBeneficii = async () => {
  return _withListCache('beneficii', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/beneficii');
    return response.data;
  });
};

// Obține ultimele 5 meditații cu progress >= 70% sau completate pentru un user
// Nu cache-uim — se schimbă des, pe măsură ce userul ascultă.
export const fetchRecentMeditations = async (userId) => {
  if (!userId) {
    return [];
  }
  try {
    const response = await API.get(`/api/funnel-events/user/${userId}/recent`);
    return response.data || [];
  } catch (error) {
    console.warn('Eroare la obținerea meditațiilor recente:', error);
    return [];
  }
};

export const fetchEmotii = async () => {
  return _withListCache('emotii', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/emotii');
    return response.data;
  });
};

export const fetchCategorii = async () => {
  return _withListCache('categorii', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/categorii');
    return response.data;
  });
};

export const fetchAbonamente = async () => {
  const response = await API.get('/abonamente');
  return response.data;
};

// Nu cache-uim — URL-ul poate fi presemnat și expira.
export const fetchAudioUrl = async (meditationId) => {
  const response = await API.get(`/api/audio/${meditationId}`);
  return response.data;
};

export const fetchHomeRecommendations = async (userId) => {
  return _withListCache(`home-recs:${userId}`, LIST_CACHE_TTL_MS, async () => {
    const response = await API.get(`/api/home/recommendations/${userId}`);
    return response.data;
  });
};

export const fetchHomePopular = async () => {
  return _withListCache('home-popular', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/api/home/popular');
    return response.data;
  });
};

export const fetchAllPopular = async () => {
  return _withListCache('home-popular-all', LIST_CACHE_TTL_MS, async () => {
    const response = await API.get('/api/home/popular?all=true');
    return response.data;
  });
};

export const fetchHomePersonalized = async (userId) => {
  return _withListCache(`home-personalized:${userId}`, LIST_CACHE_TTL_MS, async () => {
    const response = await API.get(`/api/home/personalized/${userId}`);
    return response.data;
  });
};
